import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { logAuthEvent, isDesktop } from "@/utils/authUtils";
import SignInModal from "@/components/auth/SignInModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  User,
  Shield,
  LogOut,
  CreditCard,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export default function UserMenu() {
  const { user, signOut, isAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [creditsData, setCreditsData] = useState<{balance: number, used: number, total: number} | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [hasPaidPlan, setHasPaidPlan] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);

  const fetchUserData = useCallback(async () => {
    if (!user) return;

    try {
      setIsRefreshing(true);

      console.log("Fetching credits data for user:", user.id);

      // Get user credits information
      const { data: creditsResult, error: creditsError } = await supabase
        .from("user_credits")
        .select("credits_balance, credits_used")
        .eq("user_id", user.id)
        .single();

      if (creditsError) {
        console.error("Error fetching user credits:", creditsError);
      } else if (creditsResult) {
        const total = creditsResult.credits_balance + creditsResult.credits_used;
        setCreditsData({
          balance: creditsResult.credits_balance,
          used: creditsResult.credits_used,
          total: total
        });
        console.log("Credits data:", creditsResult);
      }

      setLastRefreshed(new Date());

      // Check if user has paid subscription with more detailed query
      console.log("Checking subscription status for user:", user.id);
      const { data: subscriptionData, error: subError } = await supabase
        .from("user_subscriptions")
        .select(
          `
          id,
          plan_id,
          status,
          stripe_subscription_id,
          pricing_plan:pricing_plans(type, name)
        `
        )
        .eq("user_id", user.id)
        .eq("status", "active")
        .limit(1)
        .single();

      if (subError) {
        if (subError.code === "PGRST116") {
          console.log("No active subscription found for user:", user.id);
          setHasPaidPlan(false);
          setSubscriptionPlan(null);
        } else {
          console.error("Error fetching user subscription:", subError);
        }
      } else {
        console.log("Subscription data:", subscriptionData);

        const planData = subscriptionData?.pricing_plan;
        if (planData) {
          const planType = planData.type;
          const planName = planData.name;

          if (planType !== "free") {
            setHasPaidPlan(true);
            setSubscriptionPlan(planName);
          } else {
            setHasPaidPlan(false);
            setSubscriptionPlan("Free");
          }
        }
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user, fetchUserData]);

  useEffect(() => {
    if (!isOpen || !user) return;

    const intervalId = setInterval(() => {
      fetchUserData();
    }, 10000);

    return () => clearInterval(intervalId);
  }, [isOpen, user, fetchUserData]);

  if (!user) {
    return (
      <>
        <Button 
          variant="secondary" 
          size="sm"
          onClick={() => setShowSignInModal(true)}
        >
          <User className="mr-2 h-4 w-4" />
          Login
        </Button>
        <SignInModal
          isOpen={showSignInModal}
          onClose={() => setShowSignInModal(false)}
        />
      </>
    );
  }

  const initials = user.email ? user.email.substring(0, 2).toUpperCase() : "CJ";

  const lastRefreshedText = lastRefreshed
    ? `Last updated: ${lastRefreshed.toLocaleTimeString()}`
    : "";

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-9 w-9 rounded-full bg-primary/10 p-0"
        >
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-primary/10 text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>
          {hasPaidPlan && (
            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-green-500 border border-white"></span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="flex items-center justify-start gap-2 p-2">
          <div className="flex flex-col space-y-0.5">
            <p className="text-sm font-medium flex items-center gap-2">
              {user.email}
              {hasPaidPlan && (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                  Paid Plan
                </span>
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              {isAdmin ? "Administrator" : "User"}
              {subscriptionPlan && ` • ${subscriptionPlan}`}
            </p>
          </div>
        </div>

        {creditsData !== null && (
          <div className="px-2 py-1.5">
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2 bg-primary/10 px-3 py-1.5 rounded-md">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">
                    {creditsData.balance} / {creditsData.total} credits
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    fetchUserData();
                  }}
                  disabled={isRefreshing}
                  title="Refresh credits"
                >
                  <RefreshCw
                    className={`h-3 w-3 ${isRefreshing ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>

              {lastRefreshedText && (
                <p className="text-xs text-muted-foreground ml-2">
                  {lastRefreshedText}
                </p>
              )}
            </div>
          </div>
        )}

        {hasPaidPlan && (
          <div className="px-2 py-1.5">
            <div className="flex items-center bg-green-50 px-3 py-2 rounded-md">
              <CreditCard className="h-4 w-4 text-green-600 mr-2" />
              <span className="text-sm text-green-700">
                Active Premium Subscription
              </span>
            </div>
          </div>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link
            to="/pricing"
            className="flex cursor-pointer items-center gap-2"
            onClick={() => setIsOpen(false)}
          >
            <CreditCard className="h-4 w-4" />
            Subscription Plans
          </Link>
        </DropdownMenuItem>

        {isAdmin && (
          <DropdownMenuItem asChild>
            <Link
              to="/admin"
              className="flex cursor-pointer items-center gap-2"
              onClick={() => setIsOpen(false)}
            >
              <Shield className="h-4 w-4" />
              Admin Dashboard
            </Link>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="flex cursor-pointer items-center gap-2 text-destructive focus:text-destructive"
          onClick={async () => {
            logAuthEvent("User clicked logout button", { platform: isDesktop() ? 'desktop' : 'mobile' });
            setIsOpen(false);
            
            try {
              await signOut();
            } catch (error) {
              logAuthEvent("Logout failed in UserMenu", error);
            }
          }}
        >
          <LogOut className="h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
